/**
 * MP4 Muxer using mp4-muxer library
 *
 * Creates proper MP4 files from WebCodecs encoded video and audio chunks.
 * This library is specifically designed for WebCodecs output.
 */

import { Muxer, ArrayBufferTarget } from "mp4-muxer";

/**
 * MP4 Muxer class for creating MP4 files from encoded chunks
 */
export class MP4Muxer {
  constructor(options = {}) {
    this.width = options.width || 1920;
    this.height = options.height || 1080;
    this.fps = options.fps || 30;
    this.sampleRate = options.sampleRate || 48000;
    this.channels = options.channels || 2;

    this.muxer = null;
    this.target = null;
    this.hasVideo = false;
    this.hasAudio = false;
    this.isFinalized = false;
    this.videoSampleCount = 0;
    this.audioSampleCount = 0;
  }

  /**
   * Initialize the muxer with video and audio configuration
   * @param {Object} options - Configuration options
   * @param {string} options.videoCodec - Video codec (e.g., 'avc' for H.264)
   * @param {string} options.audioCodec - Audio codec (e.g., 'aac')
   */
  init(options = {}) {
    const { videoCodec = "avc", audioCodec = null } = options;

    this.target = new ArrayBufferTarget();

    const muxerOptions = {
      target: this.target,
      fastStart: "in-memory", // Enables seeking in the output file
    };

    // Add video track config
    if (videoCodec) {
      muxerOptions.video = {
        codec: videoCodec,
        width: this.width,
        height: this.height,
      };
      this.hasVideo = true;
    }

    // Add audio track config if specified
    if (audioCodec) {
      muxerOptions.audio = {
        codec: audioCodec,
        sampleRate: this.sampleRate,
        numberOfChannels: this.channels,
      };
      this.hasAudio = true;
    }

    this.muxer = new Muxer(muxerOptions);

    return this;
  }

  /**
   * Add encoded video chunk to the muxer
   * @param {EncodedVideoChunk} chunk - Encoded video chunk from VideoEncoder
   * @param {EncodedVideoChunkMetadata} meta - Chunk metadata (contains decoderConfig on keyframes)
   */
  addVideoChunk(chunk, meta = {}) {
    if (!this.muxer || !this.hasVideo) {
      throw new Error("Video track not initialized");
    }

    this.muxer.addVideoChunk(chunk, meta);
    this.videoSampleCount++;
  }

  /**
   * Add encoded audio chunk to the muxer
   * @param {EncodedAudioChunk} chunk - Encoded audio chunk from AudioEncoder
   * @param {EncodedAudioChunkMetadata} meta - Chunk metadata
   */
  addAudioChunk(chunk, meta = {}) {
    if (!this.muxer || !this.hasAudio) {
      throw new Error("Audio track not initialized");
    }

    this.muxer.addAudioChunk(chunk, meta);
    this.audioSampleCount++;
  }

  /**
   * Finalize the MP4 file and return as Blob
   * @returns {Blob} The finalized MP4 file
   */
  finalize() {
    if (!this.muxer) throw new Error("Muxer not initialized");
    if (this.isFinalized) throw new Error("Muxer already finalized");

    this.muxer.finalize();
    this.isFinalized = true;

    const buffer = this.target.buffer;
    return new Blob([buffer], { type: "video/mp4" });
  }

  /**
   * Get current progress info
   */
  getStats() {
    return {
      videoSamples: this.videoSampleCount,
      audioSamples: this.audioSampleCount,
    };
  }
}

/**
 * Map WebCodecs codec strings to mp4-muxer codec types
 */
function mapVideoCodec(codecString) {
  if (!codecString) return "avc";

  if (codecString.startsWith("avc1")) {
    return "avc";
  } else if (codecString.startsWith("hev1") || codecString.startsWith("hvc1")) {
    return "hevc";
  } else if (codecString.startsWith("vp09")) {
    return "vp9";
  } else if (codecString.startsWith("av01")) {
    return "av1";
  }

  return "avc"; // Default to H.264
}

function mapAudioCodec(codecString) {
  if (!codecString) return null;

  if (codecString.startsWith("mp4a") || codecString.includes("aac")) {
    return "aac";
  } else if (codecString.startsWith("opus")) {
    return "opus";
  }

  return "aac"; // Default to AAC
}

/**
 * Create and initialize a new MP4 muxer
 * @param {Object} options - Muxer options
 * @returns {MP4Muxer} Initialized muxer instance
 */
export function createMuxer(options = {}) {
  const muxer = new MP4Muxer({
    width: options.width,
    height: options.height,
    fps: options.fps,
    sampleRate: options.sampleRate,
    channels: options.channels,
  });

  const videoCodec = mapVideoCodec(options.videoCodec);
  const audioCodec = options.audioCodec
    ? mapAudioCodec(options.audioCodec)
    : null;

  muxer.init({
    videoCodec,
    audioCodec,
  });

  return muxer;
}
